import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readArticleTag,
    createArticleTag,
    updateArticleTag
} from '../actions/entity.article.tag';
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function ArticleTag() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [entityArticleTagTitle, setArticleTagTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredArticleSupportAllLanguages = currentEntity && currentEntity.requiredArticleSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [articleTagData, setArticleTagData] = useState(null);

    const showMediaDrawer = (fieldName) => {
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredArticleSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (articleTagData && typeof articleTagData.languages !== 'undefined' && Array.isArray(articleTagData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && articleTagData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
        }
    }, [currentEntity, articleTagData]);
    const languageOptions = !requiredArticleSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('entityArticleTag.message.read-only'));
            return;
        }
        if (typeof params.articleTagId !== 'undefined') {
            // Update the existing entity articleTag
            dispatch(updateArticleTag(params.entityId, params.articleTagId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setArticleTagTitle(t('entityArticleTag.title.edit'));
                    form.setFieldsValue(response.result);
                    setArticleTagData(response.result);
                }
                message.success(t('entityArticleTag.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createArticleTag(params.entityId, values)).then((data) => {
                message.success(t('entityArticleTag.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/article-tag/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/article-tags`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.articleTagId !== 'undefined') {
            setArticleTagTitle(t('entityArticleTag.title.edit'));
            dispatch(readArticleTag(params.entityId, params.articleTagId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setArticleTagTitle(t('entityArticleTag.title.edit'));
                    form.setFieldsValue(response.result);
                    setArticleTagData(response.result);
                } else {
                    message.error(t('entityArticleTag.message.error-reading'));
                    navigate(`/entity/${params.entityId}/article-tags`);
                }
            }).catch(e => {
                message.error(t('entityArticleTag.message.error-reading'));
                navigate(`/entity/${params.entityId}/article-tags`);
            });
        } else {
            setArticleTagTitle(t('entityArticleTag.title.new'));
        }
    }, [params.articleTagId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/article-tags`}>{t('entityArticleTag.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.articleTagId ? t('entityArticleTag.actions.update') : t('entityArticleTag.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-article-tag"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/article-tags`)}
                            title={entityArticleTagTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('entityArticleTag.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('entityArticleTag.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('entityArticleTag.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('entityArticleTag.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('entityArticleTag.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('entityArticleTag.field.slug.note')}</div>
                            </Form.Item>
                            {!requiredArticleSupportAllLanguages && <Form.Item
                                label={t('entityArticleTag.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('entityArticleTag.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('entityArticleTag.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('entityArticleTag.field.name.label')}
                                            name={[language.value, 'name']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('entityArticleTag.field.name.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('entityArticleTag.field.name.placeholder')} />
                                        </Form.Item>
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default ArticleTag;